import { WEB_BASE_URL } from '../config/appVariant';
import { supabase } from './supabase';

export interface TopItem {
  name: string;
  value: number;
}

export interface ShareCardData {
  totalValue: number;
  itemCount: number;
  topItems?: TopItem[];
  userName?: string;
}

export interface ShareCard {
  id: string;
  short_code: string;
  total_value: number;
  item_count: number;
  top_items: TopItem[];
  user_name: string | null;
  created_at: string;
  view_count: number;
}

function generateShortCode(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

export async function createShareCard(data: ShareCardData): Promise<{ shortCode: string; url: string } | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.error('User not authenticated');
      return null;
    }

    const shortCode = generateShortCode();
    
    // Using 'as any' because share_cards table types aren't generated yet
    const { error } = await (supabase as any)
      .from('share_cards')
      .insert({
        user_id: user.id,
        short_code: shortCode,
        total_value: data.totalValue,
        item_count: data.itemCount,
        top_items: data.topItems || [],
        user_name: data.userName || null,
      });

    if (error) {
      // If short code collision, try again with new code
      if (error.code === '23505') {
        return createShareCard(data);
      }
      console.error('Error creating share card:', error);
      return null;
    }

    // Public web origin for this deployment (prod: discovercache.com)
    const baseUrl = WEB_BASE_URL;
    
    return {
      shortCode,
      url: `${baseUrl}/share/${shortCode}`,
    };
  } catch (error) {
    console.error('Error creating share card:', error);
    return null;
  }
}

export async function getShareCard(shortCode: string): Promise<ShareCard | null> {
  try {
    // Use RPC function for controlled public access (bypasses RLS safely)
    const { data, error } = await (supabase as any)
      .rpc('get_share_card_by_code', { code: shortCode })
      .single();

    if (error || !data) {
      console.error('Error fetching share card:', error);
      return null;
    }

    // Increment view count (fire and forget)
    (supabase as any).rpc('increment_share_view', { code: shortCode }).then(() => {}).catch(() => {});

    return {
      id: data.id,
      short_code: data.short_code,
      total_value: data.total_value,
      item_count: data.item_count,
      top_items: data.top_items as TopItem[],
      user_name: data.user_name,
      created_at: data.created_at,
      view_count: data.view_count,
    };
  } catch (error) {
    console.error('Error fetching share card:', error);
    return null;
  }
}

export function getShareUrl(shortCode: string): string {
  return `${WEB_BASE_URL}/share/${shortCode}`;
}
